import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import InputAdornment from "@material-ui/core/InputAdornment";

import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";

import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal/";

const DEFAULT_FUNNEL_COLOR = "#4A6CF7";
const DEFAULT_STAGE_COLOR = "#5C59A0";
const LOCK_TOOLTIP =
  "Etapa usada pela automação de vendas do ERP — nome e exclusão travados";

// Tipos de etapa (kind). Validado também no backend (open|won|lost).
const STAGE_KINDS = [
  { value: "open", label: "Aberto" },
  { value: "won", label: "Ganho" },
  { value: "lost", label: "Perdido" },
];

// Gatilhos LOCAIS de automação (F3). Linhas fixas — o backend valida a mesma
// whitelist (CRM_FUNNEL_AUTOMATION_TRIGGERS). O destino é sempre por ID de
// funil/etapa escolhido nos selects: nenhum nome é especial aqui.
const LOCAL_AUTOMATIONS = [
  { trigger: "appointment_created", label: "Ao criar agendamento" },
  { trigger: "ticket_opened", label: "Ao abrir/aceitar conversa" },
];

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(3),
    overflowY: "auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  headerText: {
    flex: 1,
  },
  funnelCard: {
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  funnelHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
  },
  funnelName: {
    fontWeight: 600,
  },
  funnelCounts: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  spacer: {
    flex: 1,
  },
  stagesArea: {
    padding: theme.spacing(0, 2, 2),
  },
  stageRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  colorInput: {
    width: 38,
    height: 38,
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    flexShrink: 0,
  },
  nameField: {
    flex: 1,
    minWidth: 160,
  },
  kindSelect: {
    minWidth: 110,
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  emptyMsg: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0),
  },
  automationsCard: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  automationRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  automationLabel: {
    minWidth: 200,
  },
  automationSelect: {
    minWidth: 170,
  },
}));

// Tela "Funis" (F2): gestor de funis e etapas. Escala p/ N funis — nenhum
// nome é especial aqui; toda referência é por id. As automações de VENDA
// (abrir/assinar/faturar) continuam configuradas no ERP: esta tela não
// configura gatilho nenhum, só estrutura (funis, etapas, ordem, tipo).
const CrmFunnels = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  // Mesma régua do backend (middleware canManageStages): admin/owner ou o
  // flag crmManageStages do usuário.
  const canManage =
    user?.profile === "admin" ||
    user?.profile === "owner" ||
    user?.crmManageStages === true;

  // Embed (aba do ERP): preenche a altura toda; fora do embed desconta a
  // topbar (padrão das outras páginas CRM).
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [funnels, setFunnels] = useState([]);
  const [stagesByFunnel, setStagesByFunnel] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  // Dialog de funil (criar/renomear/cor).
  const [funnelDialogOpen, setFunnelDialogOpen] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState(null);
  const [funnelName, setFunnelName] = useState("");
  const [funnelColor, setFunnelColor] = useState(DEFAULT_FUNNEL_COLOR);

  const [deletingFunnel, setDeletingFunnel] = useState(null);
  const [confirmFunnelOpen, setConfirmFunnelOpen] = useState(false);

  // Drafts de edição de etapa ({stageId: {name, color, kind}}) e de etapa
  // nova por funil ({funnelId: {name, color}}).
  const [stageDrafts, setStageDrafts] = useState({});
  const [newStageDrafts, setNewStageDrafts] = useState({});
  const [deletingStage, setDeletingStage] = useState(null);
  const [confirmStageOpen, setConfirmStageOpen] = useState(false);

  // Automações locais (F3): draft por trigger ({funnelId, targetStageId,
  // enabled}). funnelId é só estado de UI (deriva de stage.funnelId no GET);
  // o backend persiste apenas trigger/targetStageId/enabled.
  const [automationDrafts, setAutomationDrafts] = useState({});

  const expandedIdRef = useRef(expandedId);
  expandedIdRef.current = expandedId;

  const fetchFunnels = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/funnels");
      setFunnels(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchStages = useCallback(async (funnelId) => {
    if (!funnelId) return;
    try {
      const { data } = await api.get("/crm/stages", {
        params: { funnelId },
      });
      const stages = Array.isArray(data) ? data : [];
      setStagesByFunnel((prev) => ({ ...prev, [funnelId]: stages }));
      setStageDrafts((prev) => {
        const next = { ...prev };
        stages.forEach((stage) => {
          next[stage.id] = {
            name: stage.name,
            color: stage.color || DEFAULT_STAGE_COLOR,
            kind: stage.kind || "open",
          };
        });
        return next;
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  // Carrega as automações e garante as etapas dos funis referenciados (os
  // selects de etapa precisam das opções pra exibir o valor salvo).
  const fetchAutomations = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/funnel-automations");
      const list = Array.isArray(data) ? data : [];
      setAutomationDrafts(() => {
        const next = {};
        list.forEach((automation) => {
          next[automation.trigger] = {
            funnelId: automation.stage?.funnelId || "",
            targetStageId: automation.targetStageId,
            enabled: automation.enabled !== false,
          };
        });
        return next;
      });
      list.forEach((automation) => {
        if (automation.stage?.funnelId) fetchStages(automation.stage.funnelId);
      });
    } catch (err) {
      toastError(err);
    }
  }, [fetchStages]);

  useEffect(() => {
    fetchFunnels();
    fetchAutomations();
  }, [fetchFunnels, fetchAutomations]);

  // Sockets: qualquer mudança em funil/estágio (inclusive de outro usuário)
  // recarrega a lista e as etapas do funil aberto.
  useEffect(() => {
    const socket = openSocket();

    const refresh = () => {
      fetchFunnels();
      if (expandedIdRef.current) fetchStages(expandedIdRef.current);
    };

    socket.on("crmFunnel", (data) => {
      if (
        data.action === "delete" &&
        expandedIdRef.current === +data.funnelId
      ) {
        setExpandedId(null);
      }
      refresh();
    });
    socket.on("crmStage", refresh);
    socket.on("crmFunnelAutomation", fetchAutomations);

    return () => socket.disconnect();
  }, [fetchFunnels, fetchStages, fetchAutomations]);

  const toggleExpand = (funnelId) => {
    const next = expandedId === funnelId ? null : funnelId;
    setExpandedId(next);
    if (next && !stagesByFunnel[next]) fetchStages(next);
  };

  // ---- Funis ----

  const openNewFunnel = () => {
    setEditingFunnel(null);
    setFunnelName("");
    setFunnelColor(DEFAULT_FUNNEL_COLOR);
    setFunnelDialogOpen(true);
  };

  const openEditFunnel = (funnel) => {
    setEditingFunnel(funnel);
    setFunnelName(funnel.name);
    setFunnelColor(funnel.color || DEFAULT_FUNNEL_COLOR);
    setFunnelDialogOpen(true);
  };

  const handleSaveFunnel = async () => {
    const name = funnelName.trim();
    if (!name) {
      toast.error("Informe o nome do funil.");
      return;
    }
    try {
      if (editingFunnel) {
        await api.put(`/crm/funnels/${editingFunnel.id}`, {
          name,
          color: funnelColor,
        });
        toast.success("Funil salvo com sucesso.");
      } else {
        await api.post("/crm/funnels", { name, color: funnelColor });
        toast.success("Funil criado com sucesso.");
      }
      setFunnelDialogOpen(false);
      fetchFunnels();
    } catch (err) {
      toastError(err);
    }
  };

  const handleMoveFunnel = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= funnels.length) return;
    const reordered = [...funnels];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setFunnels(reordered);
    try {
      await api.put("/crm/funnels/reorder", {
        ids: reordered.map((f) => f.id),
      });
    } catch (err) {
      toastError(err);
      fetchFunnels();
    }
  };

  const handleDeleteFunnel = async () => {
    if (!deletingFunnel) return;
    try {
      await api.delete(`/crm/funnels/${deletingFunnel.id}`);
      toast.success("Funil excluído com sucesso.");
      if (expandedId === deletingFunnel.id) setExpandedId(null);
      fetchFunnels();
    } catch (err) {
      toastError(err);
    }
    setDeletingFunnel(null);
  };

  // ---- Etapas ----

  const handleStageDraftChange = (stageId, field, value) => {
    setStageDrafts((prev) => ({
      ...prev,
      [stageId]: { ...prev[stageId], [field]: value },
    }));
  };

  const handleSaveStage = async (stage) => {
    const draft = stageDrafts[stage.id];
    if (!draft) return;
    try {
      await api.put(`/crm/stages/${stage.id}`, {
        // Etapa travada não renomeia (contrato por nome com o ERP) — o campo
        // fica desabilitado e o nome nem é enviado.
        ...(stage.isLocked ? {} : { name: draft.name }),
        color: draft.color,
        kind: draft.kind,
      });
      toast.success("Etapa salva com sucesso.");
      fetchStages(stage.funnelId);
    } catch (err) {
      toastError(err);
    }
  };

  const handleMoveStage = async (funnelId, index, direction) => {
    const stages = stagesByFunnel[funnelId] || [];
    const target = index + direction;
    if (target < 0 || target >= stages.length) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setStagesByFunnel((prev) => ({ ...prev, [funnelId]: reordered }));
    try {
      await api.put("/crm/stages/reorder", {
        funnelId,
        ids: reordered.map((s) => s.id),
      });
    } catch (err) {
      toastError(err);
      fetchStages(funnelId);
    }
  };

  const handleNewStageDraftChange = (funnelId, field, value) => {
    setNewStageDrafts((prev) => ({
      ...prev,
      [funnelId]: {
        name: "",
        color: DEFAULT_STAGE_COLOR,
        ...prev[funnelId],
        [field]: value,
      },
    }));
  };

  const handleAddStage = async (funnelId) => {
    const draft = newStageDrafts[funnelId] || {};
    const name = (draft.name || "").trim();
    if (!name) return;
    try {
      await api.post("/crm/stages", {
        name,
        color: draft.color || DEFAULT_STAGE_COLOR,
        funnelId,
      });
      toast.success("Etapa criada com sucesso.");
      setNewStageDrafts((prev) => ({
        ...prev,
        [funnelId]: { name: "", color: DEFAULT_STAGE_COLOR },
      }));
      fetchStages(funnelId);
      fetchFunnels();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteStage = async () => {
    if (!deletingStage) return;
    try {
      await api.delete(`/crm/stages/${deletingStage.id}`);
      toast.success("Etapa excluída com sucesso.");
      fetchStages(deletingStage.funnelId);
      fetchFunnels();
    } catch (err) {
      toastError(err);
    }
    setDeletingStage(null);
  };

  // ---- Automações locais (F3) ----

  // Salva no onChange (upsert por trigger no backend). Em erro, recarrega o
  // estado persistido pra UI não ficar mentindo.
  const saveAutomation = async (trigger, targetStageId, enabled) => {
    try {
      await api.put("/crm/funnel-automations", {
        trigger,
        targetStageId,
        enabled,
      });
      toast.success("Automação salva com sucesso.");
    } catch (err) {
      toastError(err);
      fetchAutomations();
    }
  };

  // Trocar o funil zera a etapa (as opções mudam) e NÃO salva ainda — só a
  // escolha da etapa persiste a regra.
  const handleAutomationFunnelChange = (trigger, funnelId) => {
    setAutomationDrafts((prev) => ({
      ...prev,
      [trigger]: {
        funnelId,
        targetStageId: "",
        enabled: prev[trigger]?.enabled !== false,
      },
    }));
    if (funnelId) fetchStages(funnelId);
  };

  const handleAutomationStageChange = (trigger, targetStageId) => {
    const enabled = automationDrafts[trigger]?.enabled !== false;
    setAutomationDrafts((prev) => ({
      ...prev,
      [trigger]: { ...prev[trigger], targetStageId, enabled },
    }));
    if (targetStageId) saveAutomation(trigger, targetStageId, enabled);
  };

  const handleAutomationToggle = (trigger, checked) => {
    const draft = automationDrafts[trigger];
    if (!draft?.targetStageId) return;
    setAutomationDrafts((prev) => ({
      ...prev,
      [trigger]: { ...prev[trigger], enabled: checked },
    }));
    saveAutomation(trigger, draft.targetStageId, checked);
  };

  if (!canManage) {
    return (
      <div className={classes.page}>
        <Typography variant="body1" color="textSecondary">
          Você não tem permissão para gerenciar funis.
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={classes.page}
      style={{ height: embed ? "100%" : "calc(100vh - 48px)" }}
    >
      <ConfirmationModal
        title="Excluir funil"
        open={confirmFunnelOpen}
        onClose={setConfirmFunnelOpen}
        onConfirm={handleDeleteFunnel}
      >
        {`Tem certeza que deseja excluir o funil "${
          deletingFunnel?.name || ""
        }"? As etapas dele serão excluídas junto. Funis com leads não podem ser excluídos.`}
      </ConfirmationModal>
      <ConfirmationModal
        title="Excluir etapa"
        open={confirmStageOpen}
        onClose={setConfirmStageOpen}
        onConfirm={handleDeleteStage}
      >
        Tem certeza? Os leads desta etapa serão realocados para a primeira etapa
        do funil.
      </ConfirmationModal>

      <Dialog
        open={funnelDialogOpen}
        onClose={() => setFunnelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {editingFunnel ? "Editar funil" : "Novo funil"}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="color"
              className={classes.colorInput}
              value={funnelColor}
              onChange={(e) => setFunnelColor(e.target.value)}
            />
            <TextField
              autoFocus
              fullWidth
              label="Nome do funil"
              value={funnelName}
              onChange={(e) => setFunnelName(e.target.value)}
              variant="outlined"
              margin="dense"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveFunnel();
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFunnelDialogOpen(false)}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveFunnel}
            color="primary"
            variant="contained"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant="h6">Funis</Typography>
          <Typography variant="body2" color="textSecondary">
            Estruture os funis e as etapas do Kanban. As automações de venda
            (abrir, assinar, faturar) continuam configuradas no ERP.
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openNewFunnel}
        >
          Novo funil
        </Button>
      </div>

      {funnels.length === 0 && (
        <Typography className={classes.emptyMsg}>
          Nenhum funil cadastrado.
        </Typography>
      )}

      {funnels.map((funnel, index) => {
        const expanded = expandedId === funnel.id;
        const stages = stagesByFunnel[funnel.id] || [];
        const newDraft = newStageDrafts[funnel.id] || {
          name: "",
          color: DEFAULT_STAGE_COLOR,
        };
        return (
          <Paper
            key={funnel.id}
            variant="outlined"
            className={classes.funnelCard}
          >
            <div className={classes.funnelHeader}>
              <IconButton size="small" onClick={() => toggleExpand(funnel.id)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <span
                className={classes.colorDot}
                style={{ background: funnel.color || DEFAULT_FUNNEL_COLOR }}
              />
              <Typography className={classes.funnelName}>
                {funnel.name}
              </Typography>
              {funnel.isDefault && (
                <Tooltip title="Funil padrão: recebe leads novos e os movimentos de card vindos do ERP. Não pode ser excluído.">
                  <Chip size="small" color="primary" label="Padrão" />
                </Tooltip>
              )}
              <Typography variant="body2" className={classes.funnelCounts}>
                {`${funnel.stagesCount ?? 0} etapas · ${
                  funnel.leadsCount ?? 0
                } leads`}
              </Typography>
              <span className={classes.spacer} />
              <Tooltip title="Mover para cima">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => handleMoveFunnel(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mover para baixo">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === funnels.length - 1}
                    onClick={() => handleMoveFunnel(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Renomear / trocar cor">
                <IconButton size="small" onClick={() => openEditFunnel(funnel)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  funnel.isDefault
                    ? "O funil padrão não pode ser excluído"
                    : "Excluir funil"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={funnel.isDefault}
                    onClick={() => {
                      setDeletingFunnel(funnel);
                      setConfirmFunnelOpen(true);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div className={classes.stagesArea}>
                {stages.map((stage, stageIndex) => {
                  const draft = stageDrafts[stage.id] || {
                    name: stage.name,
                    color: stage.color || DEFAULT_STAGE_COLOR,
                    kind: stage.kind || "open",
                  };
                  return (
                    <div key={stage.id} className={classes.stageRow}>
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={draft.color}
                        onChange={(e) =>
                          handleStageDraftChange(
                            stage.id,
                            "color",
                            e.target.value,
                          )
                        }
                      />
                      <Tooltip title={stage.isLocked ? LOCK_TOOLTIP : ""}>
                        <TextField
                          className={classes.nameField}
                          value={draft.name}
                          onChange={(e) =>
                            handleStageDraftChange(
                              stage.id,
                              "name",
                              e.target.value,
                            )
                          }
                          variant="outlined"
                          margin="dense"
                          disabled={stage.isLocked}
                          InputProps={
                            stage.isLocked
                              ? {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LockOutlinedIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }
                              : undefined
                          }
                        />
                      </Tooltip>
                      <Select
                        className={classes.kindSelect}
                        value={draft.kind}
                        onChange={(e) =>
                          handleStageDraftChange(
                            stage.id,
                            "kind",
                            e.target.value,
                          )
                        }
                        variant="outlined"
                        margin="dense"
                      >
                        {STAGE_KINDS.map((k) => (
                          <MenuItem key={k.value} value={k.value}>
                            {k.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Tooltip title="Mover para cima">
                        <span>
                          <IconButton
                            size="small"
                            disabled={stageIndex === 0}
                            onClick={() =>
                              handleMoveStage(funnel.id, stageIndex, -1)
                            }
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mover para baixo">
                        <span>
                          <IconButton
                            size="small"
                            disabled={stageIndex === stages.length - 1}
                            onClick={() =>
                              handleMoveStage(funnel.id, stageIndex, 1)
                            }
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Salvar etapa">
                        <IconButton
                          size="small"
                          onClick={() => handleSaveStage(stage)}
                        >
                          <SaveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={stage.isLocked ? LOCK_TOOLTIP : "Excluir etapa"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={stage.isLocked}
                            onClick={() => {
                              setDeletingStage(stage);
                              setConfirmStageOpen(true);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  );
                })}
                <div className={classes.addRow}>
                  <input
                    type="color"
                    className={classes.colorInput}
                    value={newDraft.color || DEFAULT_STAGE_COLOR}
                    onChange={(e) =>
                      handleNewStageDraftChange(
                        funnel.id,
                        "color",
                        e.target.value,
                      )
                    }
                  />
                  <TextField
                    className={classes.nameField}
                    value={newDraft.name || ""}
                    onChange={(e) =>
                      handleNewStageDraftChange(
                        funnel.id,
                        "name",
                        e.target.value,
                      )
                    }
                    placeholder="Nome da nova etapa"
                    variant="outlined"
                    margin="dense"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddStage(funnel.id);
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddStage(funnel.id)}
                  >
                    Adicionar
                  </Button>
                </div>
              </div>
            </Collapse>
          </Paper>
        );
      })}

      <Paper variant="outlined" className={classes.automationsCard}>
        <Typography variant="h6">Automações</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Movem o lead no Kanban. As automações de venda (abrir, assinar,
          faturar) continuam no ERP.
        </Typography>
        {LOCAL_AUTOMATIONS.map(({ trigger, label }) => {
          const draft = automationDrafts[trigger] || {
            funnelId: "",
            targetStageId: "",
            enabled: true,
          };
          const stages = stagesByFunnel[draft.funnelId] || [];
          const stageValue = stages.some((s) => s.id === draft.targetStageId)
            ? draft.targetStageId
            : "";
          return (
            <div key={trigger} className={classes.automationRow}>
              <Tooltip
                title={
                  draft.targetStageId
                    ? ""
                    : "Escolha o funil e a etapa de destino para ativar"
                }
              >
                <span>
                  <Switch
                    color="primary"
                    checked={Boolean(draft.targetStageId) && draft.enabled}
                    disabled={!draft.targetStageId}
                    onChange={(e) =>
                      handleAutomationToggle(trigger, e.target.checked)
                    }
                  />
                </span>
              </Tooltip>
              <Typography className={classes.automationLabel}>
                {label}
              </Typography>
              <Select
                className={classes.automationSelect}
                value={draft.funnelId || ""}
                onChange={(e) =>
                  handleAutomationFunnelChange(trigger, e.target.value)
                }
                variant="outlined"
                margin="dense"
                displayEmpty
                renderValue={(value) => {
                  const funnel = funnels.find((f) => f.id === value);
                  return funnel ? funnel.name : "Funil…";
                }}
              >
                {funnels.map((funnel) => (
                  <MenuItem key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                className={classes.automationSelect}
                value={stageValue}
                onChange={(e) =>
                  handleAutomationStageChange(trigger, e.target.value)
                }
                variant="outlined"
                margin="dense"
                displayEmpty
                disabled={!draft.funnelId}
                renderValue={(value) => {
                  const stage = stages.find((s) => s.id === value);
                  return stage ? stage.name : "Etapa…";
                }}
              >
                {stages.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          );
        })}
      </Paper>
    </div>
  );
};

export default CrmFunnels;
